import React, { useMemo } from 'react';

// Enumerates CSS custom properties from the loaded stylesheets at runtime so the
// page never goes stale when styles-v2 regenerates tokens.css / bravo-ds.css.
function collectCustomProperties() {
  const props = [];
  const seen = new Set();
  Array.from(document.styleSheets).forEach((sheet) => {
    let rules = null;
    try {
      rules = sheet.cssRules;
    } catch (e) {
      return; // cross-origin sheet — skip
    }
    if (!rules) return;
    Array.from(rules).forEach((rule) => {
      // instanceof both narrows the type for the checker and guards non-style rules
      if (!(rule instanceof CSSStyleRule) || rule.selectorText !== ':root') return;
      Array.from(rule.style).forEach((propertyName) => {
        const name = String(propertyName);
        if (name.indexOf('--') === 0 && !seen.has(name)) {
          seen.add(name);
          props.push({ name, value: rule.style.getPropertyValue(name).trim() });
        }
      });
    });
  });
  return props;
}

function looksLikeColor(value) {
  return (
    value.indexOf('#') === 0 ||
    value.indexOf('rgb') === 0 ||
    value.indexOf('hsl') === 0 ||
    value.indexOf('color-mix') === 0 ||
    value.indexOf('var(--') === 0
  );
}

function groupTokens(tokens) {
  const groups = { Colors: [], 'Radius & shadows': [], 'Spacing & sizing': [], Typography: [], Motion: [], Other: [] };
  tokens.forEach((t) => {
    if (t.name.indexOf('--radius') === 0 || t.name.indexOf('--shadow') === 0) groups['Radius & shadows'].push(t);
    else if (
      t.name.indexOf('--pad') === 0 ||
      t.name.indexOf('--gap') === 0 ||
      t.name.indexOf('--row-h') === 0 ||
      t.name.indexOf('--control-h') === 0 ||
      t.name.indexOf('--hit') === 0
    )
      groups['Spacing & sizing'].push(t);
    else if (t.name.indexOf('--font') === 0 || t.name.indexOf('--display') === 0) groups.Typography.push(t);
    else if (t.name.indexOf('--ease') === 0 || t.name.indexOf('--dur') === 0) groups.Motion.push(t);
    else if (looksLikeColor(t.value)) groups.Colors.push(t);
    else groups.Other.push(t);
  });
  return groups;
}

function TokensDemoComponent() {
  const groups = useMemo(() => groupTokens(collectCustomProperties()), []);
  const groupNames = Object.keys(groups).filter((g) => groups[g].length > 0);
  if (groupNames.length === 0) {
    return <div>No CSS custom properties found in loaded stylesheets.</div>;
  }
  return (
    <div>
      {groupNames.map((groupName) => (
        <div
          key={groupName}
          className='dsg-row'
        >
          <div className='dsg-demo-label'>{groupName}</div>
          {groupName === 'Colors' ? (
            <div className='dsg-swatches'>
              {groups[groupName].map((t) => (
                <div
                  key={t.name}
                  className='dsg-swatch'
                >
                  <div
                    className='dsg-swatch-color'
                    style={{ background: 'var(' + t.name + ')' }}
                  />
                  <div className='dsg-swatch-meta'>
                    <code>{t.name}</code>
                    <span>{t.value}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <table className='dsg-token-table'>
              <tbody>
                {groups[groupName].map((t) => (
                  <tr key={t.name}>
                    <td>
                      <code>{t.name}</code>
                    </td>
                    <td>{t.value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      ))}
    </div>
  );
}

export const tokensDemo = {
  id: 'tokens',
  title: 'Design tokens',
  category: 'Foundation',
  importPath: 'src/Css/design-system/tokens.css',
  notes: [
    'tokens.css is the authoritative token file, loaded first in index.js (before bravo-ds.css and bravo-app.css).',
    'Values below are read live from the loaded stylesheets — this page never goes stale when styles-v2 regenerates.',
    'Always use var(--token) in new CSS; never hardcode brand colors.',
  ],
  snippet:
    '.my-panel {\n  background: var(--surface);\n  border: 1px solid var(--line);\n  border-radius: var(--radius);\n  color: var(--ink);\n}',
  render: TokensDemoComponent,
};
